import { useMemo } from 'react';

import {
  useGetTaskWorktimeQuery,
  useTaskFrameStateQuery,
  useTaskObjectQuery,
} from '@/hook';
import {
  CenterFocusWeak,
  Groups,
  History,
  RestartAlt,
  Schedule,
  VFlex,
  VGrid,
  VStack,
  VideoLabel,
} from '@/package';
import type { FrameStateStatistic, Task, User } from '@/type';
import { makeAvgTime } from '@/util';

import {
  StatisticSummaryCard,
  calculateTotalFrame,
  calculateTotalObject,
  calculateUserCount,
} from '../Statistic';
import { DoughnutChart } from '../Statistic/DoughnutChart';

export const TaskStatisticSummary = ({
  taskDetail: task,
  selectedMember,
}: {
  taskDetail: Task;
  selectedMember?: User['id'];
}) => {
  const { data: taskObject } = useTaskObjectQuery(
    task.projectID,
    task.id,
    selectedMember,
    true,
  );
  const { data: taskState } = useTaskFrameStateQuery(
    task.projectID,
    task.id,
    selectedMember,
  );
  const { data: taskWorktime } = useGetTaskWorktimeQuery({
    projectID: task.projectID,
    taskID: task.id,
    workerID: selectedMember,
  });

  const avgTime = useMemo(() => {
    if (!taskWorktime?.[0]) {
      return {
        work: '0m 0s',
        review: '0m 0s',
        rework: '0m 0s',
      };
    }

    return makeAvgTime(taskWorktime[0]);
  }, [taskWorktime]);
  const frameProgress = (taskState as FrameStateStatistic) ?? {
    done: 0,
    prework: 0,
    reject: 0,
    review: 0,
    reviewing: 0,
    working: 0,
  };
  const totalFrameCount = calculateTotalFrame(frameProgress);
  const totalObjectCount = calculateTotalObject(taskObject?.statExtendClass);
  const totalUserCount = calculateUserCount(
    task?.userTask,
    (cur: { user?: { role?: string } }) => cur.user?.role,
  );

  return (
    <VStack gap={1.5} className="w-full">
      <div className="text-headline-md font-bold text-blue-300">Summary</div>

      <VGrid cols={4} gap={1}>
        <StatisticSummaryCard
          Icon={Groups}
          className="col-span-2"
          content={() => (
            <VFlex
              className="text-body-md text-gray-400"
              items="center"
              gap={0.25}
            >
              <span className="font-normal">Manager</span>
              <span className="text-body-lg font-semibold text-black">
                {Intl.NumberFormat().format(totalUserCount.manager)}
              </span>

              <div className="h-4 w-px bg-gray-300" />

              <span className="font-normal">Labeler</span>
              <span className="text-body-lg font-semibold text-black">
                {Intl.NumberFormat().format(totalUserCount.worker)}
              </span>
            </VFlex>
          )}
          title="Members"
        />

        <VStack
          gap={0.5}
          className="col-span-2 row-span-2 rounded-md px-4 py-2 shadow-sm"
        >
          <VFlex className="w-full justify-between">
            <VFlex className="items-center">
              <History className="size-5" />
              Total frames progress
            </VFlex>

            <div className="text-body-sm text-gray-600">
              Total {Intl.NumberFormat().format(totalFrameCount)}
            </div>
          </VFlex>

          <VFlex className="w-full justify-center">
            <DoughnutChart
              totalTask={totalFrameCount}
              taskProgress={{
                completed: frameProgress.done,
                fail: 0,
                none: 0,
                pending: 0,
                progress:
                  frameProgress.working +
                  frameProgress.reviewing +
                  frameProgress.review +
                  frameProgress.reject,
                ready: frameProgress.prework,
              }}
            />
          </VFlex>
        </VStack>

        <StatisticSummaryCard
          Icon={VideoLabel}
          content={() => Intl.NumberFormat().format(totalFrameCount)}
          title="Work Frames"
        />
        <StatisticSummaryCard
          Icon={CenterFocusWeak}
          content={() => Intl.NumberFormat().format(totalObjectCount)}
          title="Total Objects"
        />

        <StatisticSummaryCard
          Icon={Schedule}
          content={() => avgTime.work}
          title="Avg Work Time"
        />
        <StatisticSummaryCard
          Icon={Schedule}
          content={() => avgTime.review}
          title="Avg Review Time"
        />
        <StatisticSummaryCard
          Icon={RestartAlt}
          content={() => avgTime.rework}
          title="Avg Rework Time"
        />
      </VGrid>
    </VStack>
  );
};

TaskStatisticSummary.displayName = 'TaskStatisticSummary';
